import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import Header from './Components/Header'
import Popup from '@/components/Common/Popup'
import sidebarMenu from './Components/SidebarMenuItems'
import StyledBgContainer from '@/components/StyledBgContainer/StyledBgContainer'
import useLocalStorage from '@/hooks/useLocalStorage'
import useNotificationCount from '../Notification/services/hooks/useNotificationCount'
import { useWebSocket } from '@/context/WebSocketContext'
import Path from '@/router/localRoutes'
import './Dashboard.css'

interface BellBadgeNotification {
  count: number
}

const Dashboard = () => {
  const [roleValue] = useLocalStorage('role')

  const location = useLocation()
  const navigate = useNavigate()
  const [active, setActive] = useState<string>(getInitialActiveTab)
  const [showPopup, setShowPopup] = useState<boolean>(false)
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false)
  const { data: notificationCount } = useNotificationCount()
  const [count, setCount] = useState<number>(
    notificationCount?.data?.count ?? 0,
  )
  const { socket } = useWebSocket()

  function getInitialActiveTab() {
    const params = new URLSearchParams(location.search)
    return params.get('tab') || 'dashboard'
  }

  const fetchCount = useCallback(() => {
    setCount(notificationCount?.data?.count ?? 0)
  }, [notificationCount])

  const menuItems =
    roleValue === 'global-admin'
      ? sidebarMenu
      : sidebarMenu.filter((item) => item.tabName !== 'sub-admin')

  const getQueryParam = useCallback(
    (param: string) => {
      const params = new URLSearchParams(location.search)
      return params.get(param)
    },
    [location.search],
  )

  const handleLogout = () => {
    setShowPopup(false)
    socket?.disconnect()
    localStorage.clear()
    navigate(Path.Root)
  }

  const handleNotification = () => {
    navigate(Path.Notification)
  }

  const handleMenuClick = (itemName: string) => {
    setActive(itemName)
    setSidebarOpen(false)
    navigate(`?tab=${itemName}`)
  }

  const handleClickSetting = () => {
    setActive('setting')
    navigate(`${Path.AdminDashboard}?tab=setting`)
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const currentTab = params.get('tab')
    if (currentTab && currentTab !== active) {
      setActive(currentTab)
    }
  }, [location.search, active])

  useEffect(() => {
    if (!getQueryParam('tab')) {
      navigate(`?tab=${active}`)
    }
  }, [active, navigate, getQueryParam])

  useEffect(() => {
    fetchCount()
  }, [notificationCount])

  useEffect(() => {
    if (socket) {
      const handleBellBadgeNotification = (data: BellBadgeNotification) => {
        setCount(data.count)
      }

      socket.on('bell-badge-notification', handleBellBadgeNotification)

      return () => {
        socket.off('bell-badge-notification', handleBellBadgeNotification)
      }
    }
  }, [socket])

  return (
    <>
      <StyledBgContainer>
        {notificationCount?.data.count !== undefined && count !== undefined && (
          <Header
            handleNotification={handleNotification}
            newNotificationCounts={count}
            handleClickSetting={handleClickSetting}
          />
        )}

        {/* Mobile top bar with hamburger */}
        <div className="mobile-topbar">
          <button
            className="mobile-menu-btn"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open navigation menu"
          >
            <span />
            <span />
            <span />
          </button>
        </div>

        {/* Mobile overlay */}
        {sidebarOpen && (
          <div
            className="mobile-sidebar-overlay"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <div className="dashboard-container">
          <div
            className={`sidebar-wrapper${sidebarOpen ? ' sidebar-open' : ''}`}
          >
            <div className="dashboard-menu h-[calc(100vh-150px)] overflow-auto gap-5">
              {menuItems.map((item) => (
                <div
                  key={item.tabName}
                  className="bg-grad before:rounded-[57px] w-[80%] flex justify-items-center cursor-pointer"
                >
                  <div
                    key={item.name}
                    className={`py-4 px-4 flex justify-items-center  ${
                      active === item.tabName
                        ? 'active-menu-item-bg'
                        : 'normal-menu-item-bg'
                    }`}
                    onClick={() => handleMenuClick(item.tabName)}
                  >
                    {React.cloneElement(<item.icon />, {
                      className: `mr-3 ${
                        active === item.tabName
                          ? item.iconFill
                            ? '[&>path]:fill-white'
                            : '[&>path]:stroke-white'
                          : item.iconFill
                            ? '[&>path]:fill-[#7d7d7d]'
                            : '[&>path]:stroke-[#7d7d7d]'
                      }`,
                    })}
                    {item.name}
                  </div>
                </div>
              ))}
            </div>
            <div
              className={`menuItem mx-auto py-3`}
              onClick={() => setShowPopup(true)}
            >
              <img alt="log-out" src="/icon/Logout.svg" />
              Log Out
            </div>
          </div>
          <div className="dashboard-main-content-wrapper">
            {menuItems.map((item) =>
              active === item.tabName ? item.component : null,
            )}
          </div>
        </div>
      </StyledBgContainer>
      {showPopup && (
        <Popup
          showYesNoButtons={true}
          icon={
            <img className="w-[30px] h-[45px]" src="./Logout.svg" alt="Mark" />
          }
          onNoClick={() => setShowPopup(false)}
          onYesClick={handleLogout}
          title={
            <>
              Are You Sure
              <br />
              You Want to Logout?
            </>
          }
          message="You are about to log out. Please confirm to proceed."
          className="!bg-[#FB5012]"
        />
      )}
    </>
  )
}

export default Dashboard
